'use client'

import React from 'react'
import styles from '../../PluginTabs.module.css'

/**
 * @uiComponent
 * @name Layouts
 * @description The Layouts component provides workspace layout sharing and configuration management capabilities.
 * It generates shareable URLs containing the current layout and widget map configuration, enabling users
 * to share their exact workspace setup with others. Features include automatic URL generation, clipboard copying, and modal notifications.
 * @location Layouts plugin in the trading terminal
 * @prop none - No external props, operates as a standalone layout sharing utility
 */

/**
 * @uiWorkflow
 * @name Share Current Layout Configuration
 * @description How to create and share a link to your current workspace layout
 * @platform layouts
 * @step 1 Navigate to the Layouts plugin [component: Layouts] [action: navigate]
 * @step 2 Click the "Layouts" button [component: Layouts] [action: click]
 * @step 3 Wait for the layout URL to be generated [component: Layouts] [action: wait] [notes: Captures current layout and widget map]
 * @step 4 Review the modal with layout sharing information [component: Layouts] [action: observe] [notes: Shows generated URL with layout data]
 * @step 5 Note that URL is automatically copied to clipboard [component: Layouts] [action: acknowledge] [notes: Ready to share immediately]
 * @step 6 Share the URL with team members [component: Layouts] [action: distribute] [notes: Via email, Slack, Teams, etc.]
 * @step 7 Close the modal when finished [component: Layouts] [action: close] [notes: Click Close button or wait for auto-close]
 * @step 8 Monitor when others access your shared layout [component: Layouts] [action: track]
 */

/**
 * @uiWorkflow
 * @name Load Shared Layout from Others
 * @description How to access and use someone else's shared layout configuration
 * @platform layouts
 * @step 1 Receive a layout link from another user [component: Layouts] [action: receive] [notes: URL contains encoded layout data]
 * @step 2 Click or navigate to the shared URL [component: Layouts] [action: navigate]
 * @step 3 Wait for the layout to load and apply [component: Layouts] [action: load] [notes: Layout and widget map restored]
 * @step 4 Observe the shared layout configuration [component: Layouts] [action: examine] [notes: Exact replica of original setup]
 * @step 5 Explore the shared widget arrangement [component: Layouts] [action: interact] [notes: All widgets positioned as shared]
 * @step 6 Adapt the layout for your own use [component: Layouts] [action: customize] [notes: Modify as needed]
 * @step 7 Save your own version if desired [component: Layouts] [action: save]
 * @step 8 Share your modified layout with others [component: Layouts] [action: re-share]
 */

/**
 * @uiWorkflow
 * @name Manage Layout Sharing and Collaboration
 * @description How to effectively use layout sharing for team collaboration
 * @platform layouts
 * @step 1 Design your optimal workspace layout [component: Layouts] [action: setup] [notes: Arrange widgets and panels]
 * @step 2 Test the layout for functionality [component: Layouts] [action: validate] [notes: Ensure all widgets work properly]
 * @step 3 Generate a shareable layout link [component: Layouts] [action: create]
 * @step 4 Document the layout purpose and features [component: Layouts] [action: document] [notes: Explain layout benefits]
 * @step 5 Share with team members or collaborators [component: Layouts] [action: distribute]
 * @step 6 Collect feedback on the layout [component: Layouts] [action: gather-feedback]
 * @step 7 Iterate and improve the layout [component: Layouts] [action: refine]
 * @step 8 Create updated shareable links [component: Layouts] [action: update]
 * @step 9 Maintain a library of useful layouts [component: Layouts] [action: organize]
 * @step 10 Train team members on layout usage [component: Layouts] [action: educate]
 */

export const Layouts = () => {
  const handleLayoutsClick = () => {
    // Get current layout and widget map from localStorage
    const layout = localStorage.getItem('myLayout');
    const widgetMap = localStorage.getItem('myWidgetMap');
    
    // Create a unique instance ID
    const instanceId = Date.now().toString(36);
    
    // Get the current symbol from the URL
    const pathParts = window.location.pathname.split('/');
    const symbol = pathParts[1] || 'BTCUSDT';
    
    // Create a shareable URL with layout and widget map as query parameters
    const shareableUrl = `${window.location.origin}/${symbol}/${instanceId}?layout=${encodeURIComponent(layout || '')}&widgetMap=${encodeURIComponent(widgetMap || '')}`;
    
    // Copy to clipboard
    navigator.clipboard.writeText(shareableUrl)
      .then(() => {
        // Create and show a modal with more information
        const modal = document.createElement('div');
        modal.style.position = 'fixed';
        modal.style.top = '50%';
        modal.style.left = '50%';
        modal.style.transform = 'translate(-50%, -50%)';
        modal.style.backgroundColor = '#1e2329';
        modal.style.padding = '20px';
        modal.style.borderRadius = '8px';
        modal.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        modal.style.zIndex = '9999';
        modal.style.maxWidth = '400px';
        modal.style.width = '90%';
        modal.style.color = '#e0e0e0';
        
        modal.innerHTML = `
          <h3 style="margin-top: 0; color: #88a1ac;">Layouts Link Created!</h3>
          <p>Share this link with others to let them view your exact layout configuration:</p>
          <div style="background: #151a1e; padding: 10px; border-radius: 4px; margin: 10px 0; word-break: break-all; font-size: 12px;">
            ${shareableUrl}
          </div>
          <p style="margin-bottom: 0;">✅ URL copied to clipboard</p>
          <p style="font-size: 12px; margin-top: 10px; color: #88a1ac;">When others open this link, they'll see your exact layout and widget configuration.</p>
          <button id="close-layouts-modal" style="background: #88a1ac; border: none; padding: 8px 16px; border-radius: 4px; margin-top: 15px; cursor: pointer; color: #151a1e;">Close</button>
        `;
        
        document.body.appendChild(modal);
        
        // Add event listener to close button
        document.getElementById('close-layouts-modal')?.addEventListener('click', () => {
          document.body.removeChild(modal);
        });
        
        // Auto-close after 10 seconds
        setTimeout(() => {
          if (document.body.contains(modal)) {
            document.body.removeChild(modal);
          }
        }, 10000);
      })
      .catch(err => console.error('Failed to copy URL:', err));
  }

  return (
    <button onClick={handleLayoutsClick} className={styles.pluginButton}>
      Layouts
    </button>
  )
}